/**
 * File Interface
 * File upload and selection interface for content forms
 */

import { useState, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    File as FileIcon,
    Image,
    Film,
    Music,
    FileText,
    Archive,
    Upload,
    FolderOpen,
    Link as LinkIcon,
    Download,
    Trash2,
    Loader2,
    Eye,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { FilePickerDialog } from './FilePickerDialog';

interface FileItem {
    id: string;
    filename_download: string;
    title: string | null;
    type: string | null;
    filesize: number | null;
    modified_on: string;
}

interface FileInterfaceProps {
    value: string | null;
    onChange: (value: string | null) => void;
    folder?: string;
    disabled?: boolean;
    className?: string;
    accept?: string;
}

// Get file icon based on MIME type
function getFileIcon(type: string | null, className = 'h-5 w-5') {
    if (!type) return <FileIcon className={className} />;
    if (type.startsWith('image/')) return <Image className={cn(className, 'text-blue-600')} />;
    if (type.startsWith('video/')) return <Film className={cn(className, 'text-purple-600')} />;
    if (type.startsWith('audio/')) return <Music className={cn(className, 'text-green-600')} />;
    if (type.includes('pdf') || type.includes('document') || type.includes('text'))
        return <FileText className={cn(className, 'text-red-600')} />;
    if (type.includes('zip') || type.includes('tar') || type.includes('compress'))
        return <Archive className={cn(className, 'text-orange-600')} />;
    return <FileIcon className={className} />;
}

// Format file size
function formatFileSize(bytes: number | null): string {
    if (bytes === null || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// Get readable mime type extension
function getFileExtension(type: string | null): string | null {
    if (!type) return null;
    const parts = type.split('/');
    return parts[1]?.toUpperCase() || null;
}

export function FileInterface({ value, onChange, folder, disabled, className, accept }: FileInterfaceProps) {
    const [file, setFile] = useState<FileItem | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
    const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);

    // Dialog states
    const [pickerDialogOpen, setPickerDialogOpen] = useState(false);
    const [urlDialogOpen, setUrlDialogOpen] = useState(false);
    const [importUrl, setImportUrl] = useState('');
    const [importLoading, setImportLoading] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Fetch file details when value changes
    useMemo(() => {
        if (value) {
            const fetchFile = async () => {
                setLoading(true);
                try {
                    const params = new URLSearchParams();
                    ['id', 'filename_download', 'title', 'type', 'filesize', 'modified_on'].forEach(field => {
                        params.append('fields[]', field);
                    });
                    const response = await api.get<{ data: FileItem }>(`/files/${value}?${params.toString()}`);
                    setFile(response.data);
                } catch (error) {
                    console.error('Error fetching file:', error);
                    setFile(null);
                } finally {
                    setLoading(false);
                }
            };
            fetchFile();
        } else {
            setFile(null);
            setThumbnailUrl(null);
            setFullImageUrl(null);
        }
    }, [value]);

    // Load thumbnail and full image as blobs for authenticated access
    useMemo(() => {
        if (file && file.type?.startsWith('image/')) {
            // Load thumbnail
            const loadThumbnail = async () => {
                try {
                    const blob = await api.getBlob(`/assets/${file.id}?width=200&height=200&fit=cover&key=system-small-cover`);
                    const url = window.URL.createObjectURL(blob);
                    setThumbnailUrl(url);
                } catch (error) {
                    console.error('Error loading thumbnail:', error);
                    setThumbnailUrl(null);
                }
            };

            // Load full image
            const loadFullImage = async () => {
                try {
                    const blob = await api.getBlob(`/assets/${file.id}`);
                    const url = window.URL.createObjectURL(blob);
                    setFullImageUrl(url);
                } catch (error) {
                    console.error('Error loading full image:', error);
                    setFullImageUrl(null);
                }
            };

            loadThumbnail();
            loadFullImage();

            // Cleanup URLs on unmount
            return () => {
                if (thumbnailUrl) window.URL.revokeObjectURL(thumbnailUrl);
                if (fullImageUrl) window.URL.revokeObjectURL(fullImageUrl);
            };
        } else {
            setThumbnailUrl(null);
            setFullImageUrl(null);
        }
    }, [file]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            handleUpload(files[0]!);
        }
    };

    const handleUpload = async (selectedFile: File) => {
        if (accept && accept.includes('image/*') && selectedFile.type && !selectedFile.type.startsWith('image/')) {
            toast.error('Unsupported file format. Please upload an image file.');
            return;
        }
        setUploading(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            if (folder) {
                formData.append('folder', folder);
            }

            // Don't set Content-Type header - browser will set it with correct boundary
            const response = await api.post<{ data: FileItem }>('/files', formData);

            const uploadedFile = response.data;
            setFile(uploadedFile);
            onChange(uploadedFile.id);
            toast.success('File uploaded successfully');
        } catch (error: any) {
            console.error('Error uploading file:', error);
            toast.error(error.response?.data?.errors?.[0]?.message || 'Failed to upload file');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handlePickerSelect = (fileId: string, selectedFile: FileItem) => {
        setFile(selectedFile);
        onChange(fileId);
        toast.success('File selected');
    };

    const handleUrlImport = async () => {
        if (!importUrl) return;

        setImportLoading(true);
        try {
            const response = await api.post<{ data: FileItem }>('/files/import', {
                url: importUrl,
                data: folder ? { folder } : {},
            });

            const importedFile = response.data;
            setFile(importedFile);
            onChange(importedFile.id);
            setUrlDialogOpen(false);
            setImportUrl('');
            toast.success('File imported successfully');
        } catch (error: any) {
            console.error('Error importing file:', error);
            toast.error(error.response?.data?.errors?.[0]?.message || 'Failed to import file');
        } finally {
            setImportLoading(false);
        }
    };

    const handleRemove = () => {
        setFile(null);
        onChange(null);
        toast.success('File removed');
    };

    const handleDownload = async () => {
        if (!file) return;

        try {
            // Fetch the file with authentication
            const blob = await api.getBlob(`/assets/${file.id}?download`);

            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.filename_download || 'download';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('File downloaded');
        } catch (error: any) {
            console.error('Download error:', error);
            toast.error('Failed to download file');
        }
    };

    const isValidUrl = (url: string) => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    return (
        <div className={cn('space-y-2', className)}>
            {loading ? (
                <div className="flex items-center justify-center h-24 border border-dashed rounded-lg">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : file ? (
                <div className="border rounded-lg p-4">
                    <div className="flex items-start gap-4">
                        {/* Thumbnail or Icon */}
                        <div className="shrink-0">
                            {thumbnailUrl ? (
                                <div className="w-20 h-20 rounded overflow-hidden bg-muted">
                                    <img
                                        src={thumbnailUrl}
                                        alt={file.title || file.filename_download}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            ) : (
                                <div className="w-20 h-20 rounded bg-muted flex items-center justify-center">
                                    {getFileIcon(file.type, 'h-8 w-8')}
                                    {getFileExtension(file.type) && (
                                        <span className="absolute text-xs font-semibold">
                                            {getFileExtension(file.type)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* File Info */}
                        <div className="flex-1 min-w-0">
                            <h4 className="font-medium truncate">{file.title || file.filename_download}</h4>
                            <div className="text-sm text-muted-foreground space-y-1 mt-1">
                                <div>{formatFileSize(file.filesize)}</div>
                                {file.type && <div>{file.type}</div>}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 mt-3">
                                {thumbnailUrl && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPreviewOpen(true);
                                        }}
                                    >
                                        <Eye className="h-4 w-4 mr-1" />
                                        Preview
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownload();
                                    }}
                                >
                                    <Download className="h-4 w-4 mr-1" />
                                    Download
                                </Button>
                                {!disabled && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemove();
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4 mr-1" />
                                        Remove
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="outline"
                            className="w-full h-24 border-dashed"
                            disabled={disabled || uploading}
                        >
                            {uploading ? (
                                <div className="flex flex-col items-center gap-2">
                                    <Loader2 className="h-6 w-6 animate-spin" />
                                    <span className="text-sm">Uploading... {uploadProgress}%</span>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <Upload className="h-6 w-6" />
                                    <span className="text-sm">Choose or Upload File</span>
                                </div>
                            )}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-56">
                        <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload from Device
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPickerDialogOpen(true)}>
                            <FolderOpen className="h-4 w-4 mr-2" />
                            Choose from Library
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setUrlDialogOpen(true)}>
                            <LinkIcon className="h-4 w-4 mr-2" />
                            Import from URL
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                disabled={disabled}
                accept={accept}
            />

            {/* File Picker Dialog */}
            <FilePickerDialog
                open={pickerDialogOpen}
                onOpenChange={setPickerDialogOpen}
                onSelect={handlePickerSelect}
                selectedFileId={value}
                folder={folder}
                accept={accept}
            />

            {/* URL Import Dialog */}
            <Dialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Import from URL</DialogTitle>
                        <DialogDescription>
                            Enter a URL to import a file from the internet
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>URL</Label>
                            <Input
                                placeholder="https://example.com/file.jpg"
                                value={importUrl}
                                onChange={(e) => setImportUrl(e.target.value)}
                                disabled={importLoading}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setUrlDialogOpen(false);
                                setImportUrl('');
                            }}
                            disabled={importLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUrlImport}
                            disabled={!isValidUrl(importUrl) || importLoading}
                        >
                            {importLoading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Importing...
                                </>
                            ) : (
                                'Import'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Image Preview Dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle>{file?.title || file?.filename_download || 'Image Preview'}</DialogTitle>
                    </DialogHeader>
                    {file && fullImageUrl && (
                        <div className="flex items-center justify-center overflow-auto">
                            <img
                                src={fullImageUrl}
                                alt={file.title || file.filename_download}
                                className="max-w-full h-auto"
                            />
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setPreviewOpen(false)}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
